/**
 * palette.js — Workload palette and tile management
 *
 * Owns:
 *  - Palette group configuration (greenfieldPaletteGroups, vaultPaletteGroups)
 *  - Workload mutation functions: add, update, remove for palette tiles
 *  - wirePaletteTileEvents — qty/label/remove button wiring (called after each render)
 *  - wirePaletteDropZones — HTML5 drag-drop zone wiring (partially implemented;
 *    dragstart handler not yet wired so payload is always null at runtime)
 *  - Workload query helpers: getWorkloadRowId, getSiteWorkloadByRowId,
 *    getSiteWorkloadByIdentifier
 *  - Validation predicates: siteHasOnlyInDevelopmentWorkloads,
 *    hasOnlyInDevelopmentWorkloads, hasPendingWorkloadSelection
 *
 * Extracted from the §PALETTE section of script.js (was lines 449–742).
 * renderSiteList() calls replaced with emit(BUS_EVENTS.SITE_LIST_CHANGED) so
 * this module has no dependency on script.js.
 */

import { emit, BUS_EVENTS } from './bus.js';
import { sites } from './state.js';
import { workloadOptionGroups } from './workload-defs.js';
import {
    getSiteById,
    createWorkloadRowId,
    normalizeWorkloadCustomLabel,
    hasSelectedWorkloadValue,
    normalizeWorkloadIdentifier,
} from './helpers.js';
import { normalizeWorkloadValue } from './utils.js';
import { hasMeaningfulWorkloadScope } from './recalc.js';

// ── Palette configuration ──────────────────────────────────────────────────

/** Workloads available in the Greenfield palette */
export const greenfieldPaletteGroups = workloadOptionGroups;

/** Vault migration source palette */
export const vaultPaletteGroups = [
    {
        label: 'Current state Repositories',
        options: [
            { value: 'azure-blob',   label: 'Azure Blob',              unit: 'jobs', icon: null },
            { value: 'aws-s3',       label: 'AWS S3',                  unit: 'jobs', icon: null },
            { value: 'onprem-s3',    label: 'On-prem Object Storage',  unit: 'jobs', icon: null },
            { value: 'cloud-s3',     label: 'Cloud S3-compatible',     unit: 'jobs', icon: null },
            { value: 'onprem-block', label: 'On-prem Block Storage',   unit: 'jobs', icon: null },
        ]
    },
];

/** Default quantities per unit type */
const paletteDefaultQty = {
    VMs: 50, instances: 10, users: 100, clusters: 1, endpoints: 20,
    TB: 5, servers: 1, jobs: 5, proxies: 2, repositories: 2, nodes: 1,
};

// ── Workload mutation functions ────────────────────────────────────────────

/** Add a palette workload to a specific site */
export function addPaletteWorkloadToSite(siteId, { value, label, unit, duplicable, maxQty }) {
    const site = getSiteById(siteId);
    if (!site) return;

    if (!Array.isArray(site.paletteWorkloads)) {
        site.paletteWorkloads = [];
    }

    const rowId = createWorkloadRowId();
    const defaultQty = maxQty != null ? Math.min(paletteDefaultQty[unit] || 1, maxQty) : (paletteDefaultQty[unit] || 1);
    site.paletteWorkloads.push({
        rowId,
        value,
        label,
        customLabel: '',
        unit,
        qty: defaultQty,
        ...(maxQty != null && { maxQty }),
    });

    emit(BUS_EVENTS.SITE_LIST_CHANGED);
}

/** Update the custom label for a palette workload tile */
export function updatePaletteWorkloadLabel(siteId, rowId, customLabel) {
    const site = getSiteById(siteId);
    if (!site || !Array.isArray(site.paletteWorkloads)) return;
    const wl = site.paletteWorkloads.find((w) => w.rowId === rowId);
    if (wl) wl.customLabel = customLabel;
}

export function updateVaultWorkloadCustomLabel(siteId, rowId, customLabel) {
    const site = getSiteById(siteId);
    if (!site) return;
    const workload = getSiteWorkloadByIdentifier(site, rowId);
    if (!workload) return;
    const trimmed = normalizeWorkloadCustomLabel(customLabel);
    if (trimmed) {
        workload.customLabel = trimmed;
    } else {
        delete workload.customLabel;
    }
}

/** Remove a palette workload tile from a site */
export function removePaletteWorkloadFromSite(siteId, rowId) {
    const site = getSiteById(siteId);
    if (!site || !Array.isArray(site.paletteWorkloads)) return;
    site.paletteWorkloads = site.paletteWorkloads.filter((w) => w.rowId !== rowId);
    emit(BUS_EVENTS.SITE_LIST_CHANGED);
}

/** Update quantity for a palette workload tile */
export function updatePaletteWorkloadQty(siteId, rowId, newQty) {
    const site = getSiteById(siteId);
    if (!site || !Array.isArray(site.paletteWorkloads)) return;
    const wl = site.paletteWorkloads.find((w) => w.rowId === rowId);
    if (wl) {
        const parsed = Math.max(1, parseInt(newQty, 10) || 1);
        wl.qty = wl.maxQty != null ? Math.min(parsed, wl.maxQty) : parsed;
    }
}

// ── Tile event wiring ─────────────────────────────────────────────────────

/**
 * Wire palette tile interaction events after renderSiteList runs.
 * @param {object}      deps
 * @param {HTMLElement} deps.siteList - The site list DOM container
 */
export function wirePaletteTileEvents({ siteList }) {
    if (!siteList) return;

    // Qty decrement / increment buttons — handled by a shared delta handler
    function wirePaletteQtyBtn(selector, dataKey, delta) {
        siteList.querySelectorAll(selector).forEach((btn) => {
            btn.addEventListener('click', () => {
                const rowId = parseInt(btn.dataset[dataKey], 10);
                const siteId = parseInt(btn.dataset.siteId, 10);
                const site = getSiteById(siteId);
                const wl = site && Array.isArray(site.paletteWorkloads) && site.paletteWorkloads.find((w) => w.rowId === rowId);
                if (wl) {
                    wl.qty = Math.max(1, wl.qty + delta);
                    const input = siteList.querySelector(`[data-palette-qty-input="${rowId}"]`);
                    if (input) input.value = wl.qty;
                }
            });
        });
    }
    wirePaletteQtyBtn('[data-palette-qty-decrement]', 'paletteQtyDecrement', -1);
    wirePaletteQtyBtn('[data-palette-qty-increment]', 'paletteQtyIncrement', +1);

    // Qty input changes
    siteList.querySelectorAll('[data-palette-qty-input]').forEach((input) => {
        input.addEventListener('change', () => {
            const rowId = parseInt(input.dataset.paletteQtyInput, 10);
            const siteId = parseInt(input.dataset.siteId, 10);
            updatePaletteWorkloadQty(siteId, rowId, input.value);
        });
    });

    // Custom label inputs
    siteList.querySelectorAll('[data-palette-label-input]').forEach((input) => {
        input.addEventListener('input', () => {
            const rowId = parseInt(input.dataset.paletteLabelInput, 10);
            const siteId = parseInt(input.dataset.siteId, 10);
            updatePaletteWorkloadLabel(siteId, rowId, input.value);
        });
        // Prevent drag from starting when typing in the input
        input.addEventListener('mousedown', (e) => e.stopPropagation());
    });

    // Remove buttons
    siteList.querySelectorAll('[data-palette-tile-remove]').forEach((btn) => {
        btn.addEventListener('click', () => {
            const rowId = parseInt(btn.dataset.paletteTileRemove, 10);
            const siteId = parseInt(btn.dataset.siteId, 10);
            removePaletteWorkloadFromSite(siteId, rowId);
        });
    });
}

// ── Workload query helpers ─────────────────────────────────────────────────

export function getWorkloadRowId(workload) {
    if (!workload || typeof workload !== 'object') {
        return 0;
    }

    if (Number.isInteger(workload.rowId) && workload.rowId > 0) {
        return workload.rowId;
    }

    workload.rowId = createWorkloadRowId();
    return workload.rowId;
}

export function getSiteWorkloadByRowId(site, workloadRowId) {
    return Array.isArray(site?.workloads)
        ? site.workloads.find((workload) => getWorkloadRowId(workload) === workloadRowId) || null
        : null;
}

export function getSiteWorkloadByIdentifier(site, workloadIdentifier) {
    const normalizedIdentifier = normalizeWorkloadIdentifier(workloadIdentifier);
    if (!Array.isArray(site?.workloads)) {
        return null;
    }

    if (Number.isInteger(normalizedIdentifier)) {
        return getSiteWorkloadByRowId(site, normalizedIdentifier);
    }

    return site.workloads.find((workload) => workload.value === normalizedIdentifier) || null;
}

// ── Validation predicates ──────────────────────────────────────────────────

export function siteHasOnlyInDevelopmentWorkloads(site) {
    const hasAnySelected = Array.isArray(site?.workloads)
        && site.workloads.some((w) => hasSelectedWorkloadValue(normalizeWorkloadValue(w.value)));
    if (!hasAnySelected) return false;
    return !site.workloads.some((w) => hasMeaningfulWorkloadScope(w));
}

export function hasOnlyInDevelopmentWorkloads() {
    const hasAnyTile = sites.some((site) =>
        (Array.isArray(site.workloads) && site.workloads.some((w) => hasSelectedWorkloadValue(normalizeWorkloadValue(w.value))))
        || (Array.isArray(site.paletteWorkloads) && site.paletteWorkloads.length > 0)
    );
    if (!hasAnyTile) return false;
    const hasAnyValid = sites.some((site) =>
        (Array.isArray(site.workloads) && site.workloads.some((w) => hasMeaningfulWorkloadScope(w)))
        || (Array.isArray(site.paletteWorkloads) && site.paletteWorkloads.length > 0)
    );
    return !hasAnyValid;
}

export function hasPendingWorkloadSelection(site) {
    return Array.isArray(site?.workloads)
        && site.workloads.some((workload) => !hasSelectedWorkloadValue(workload?.value));
}
